/* Shared annual input estimate for the public and adviser salderingscheck. */
(function () {
  'use strict';
  const fields = ['annualImport', 'annualExport', 'annualGeneration'];
  const labels = { annualImport: 'Jaarlijks verbruik (netafname)', annualExport: 'Teruglevering', annualGeneration: 'Zonneopwek' };
  // CBS 85140NED, 2024 (nader voorlopig): all homes, gas/block heating, no PV.
  // Electricity delivery is used as a reference for demand because this group has no PV.
  const household = { 1: 1600, 2: 2410, 3: 2950, 4: 3510, 5: 3880 };
  const nf = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 0 });
  const number = value => value == null || String(value).trim() === '' ? NaN : Number(value);
  const round = value => Math.round(value / 50) * 50;
  // Change estimation assumptions here once; both pages use this same module.
  const defaults = Object.freeze({ wp: 400, yieldFactor: 0.85, selfUse: 25, householdUpliftPercent: 15 });
  // Editable starting assumptions, deliberately not presented as measured usage
  // or a universal average. Only selected devices contribute to an estimate.
  const deviceDefaults = { ev: 2500, heatpump: 3000, airco: 500, other: 1000 };
  const deviceLabels = { ev: 'Elektrische auto', heatpump: 'Warmtepomp', airco: 'Airco', other: 'Overig' };

  function deviceEstimate(input) {
    const devices = [...new Set(Array.isArray(input.devices) ? input.devices : [])];
    const deviceKwh = {};
    devices.forEach(device => {
      if (!Object.hasOwn(deviceDefaults, device)) throw new Error('Kies een van de aangegeven grote stroomverbruikers.');
      const value = input.deviceKwh?.[device] == null ? deviceDefaults[device] : number(input.deviceKwh[device]);
      if (!Number.isFinite(value) || value < 0 || value > 30000) throw new Error('Vul voor ' + deviceLabels[device].toLowerCase() + ' een extra jaarverbruik in van 0 tot en met 30.000 kWh.');
      deviceKwh[device] = value;
    });
    const extraKwh = Object.values(deviceKwh).reduce((total, value) => total + value, 0);
    if (extraKwh > 30000) throw new Error('Controleer het extra verbruik: gebruik samen maximaal 30.000 kWh per jaar.');
    return { devices, deviceKwh, extraKwh };
  }

  function knownValues(current, estimate, demoFields = []) {
    const known = {};
    fields.forEach(name => {
      if (!demoFields.includes(name) && !estimate?.fields?.includes(name)) known[name] = current[name];
    });
    return known;
  }

  function calculate(input, known = {}) {
    const people = number(input.people), panels = number(input.panels), wp = number(input.wp);
    const yieldFactor = number(input.yieldFactor), selfUse = number(input.selfUse);
    if (!Object.hasOwn(household, people)) throw new Error('Kies het aantal personen in het huishouden.');
    if (!Number.isInteger(panels) || panels < 0 || panels > 60) throw new Error('Vul een aantal zonnepanelen van 0 tot en met 60 in.');
    const { devices, deviceKwh, extraKwh } = deviceEstimate(input);
    if (!Number.isFinite(wp) || wp < 100 || wp > 600) throw new Error('Vul een paneelvermogen van 100 tot en met 600 Wp in.');
    if (![.75, .85, .95].includes(yieldFactor)) throw new Error('Kies een opbrengstfactor.');
    if (!Number.isFinite(selfUse) || selfUse < 10 || selfUse > 70) throw new Error('Vul voor direct eigen gebruik 10 tot en met 70% in.');
    const values = {}, estimated = [];
    fields.forEach(name => {
      if (known[name] == null || String(known[name]).trim() === '') return;
      const value = number(known[name]);
      if (!Number.isFinite(value) || value < 0 || value > 50000) throw new Error('Controleer de al ingevulde jaarcijfers: gebruik 0 tot en met 50.000 kWh.');
      values[name] = value;
    });
    const fill = (name, value) => { if (!Object.hasOwn(values, name)) { values[name] = round(value); estimated.push(name); } };
    fill('annualGeneration', panels * wp * yieldFactor);
    fill('annualExport', values.annualGeneration * (1 - selfUse / 100));
    if (estimated.includes('annualExport')) values.annualExport = Math.min(values.annualExport, values.annualGeneration);
    if (values.annualExport > values.annualGeneration) throw new Error('De bekende teruglevering is hoger dan de opwek. Controleer de panelen, het vermogen en of de jaarcijfers over dezelfde periode gaan.');
    const demand = household[people] * (100 + defaults.householdUpliftPercent) / 100 + extraKwh;
    const directUse = values.annualGeneration - values.annualExport;
    if (!Object.hasOwn(values, 'annualImport') && round(demand - directUse) <= 0) throw new Error('Het geschatte directe zonnegebruik is te hoog voor dit huishouden. Gebruik de werkelijke netafname om hiermee verder te rekenen.');
    fill('annualImport', demand - directUse);
    if (!estimated.length) throw new Error('Alle jaarcijfers zijn al ingevuld. Maak alleen een onbekend cijfer leeg om dat te laten schatten.');
    return { values, fields: estimated, input: { people, panels, wp, yieldFactor, selfUse, householdUpliftPercent: defaults.householdUpliftPercent, devices, deviceKwh, extraKwh }, demand };
  }

  function describe(estimate) {
    if (!estimate?.fields?.length) return '';
    const i = estimate.input;
    const parts = [estimate.fields.map(name => labels[name]).join(', ') + ' geschat.'];
    if (estimate.fields.includes('annualGeneration')) parts.push('Opwek: ' + i.panels + ' panelen × ' + i.wp + ' Wp × opbrengstfactor ' + String(i.yieldFactor).replace('.', ',') + '.');
    if (estimate.fields.includes('annualExport')) parts.push('Teruglevering: opwek minus ' + i.selfUse + '% direct eigen gebruik.');
    if (estimate.fields.includes('annualImport')) parts.push('Netafname: CBS-verbruiksreferentie voor ' + i.people + (i.people === 5 ? '+' : '') + ' personen (2024, nader voorlopig)' + (i.householdUpliftPercent ? ' met ' + i.householdUpliftPercent + '% opslag als ZinVolt-rekenaanname' : '') + ', plus ' + nf.format(i.extraKwh) + ' kWh extra verbruik, minus direct gebruikte zonnestroom.');
    if (estimate.fields.includes('annualImport') && i.devices?.length) parts.push('Bewerkbare rekenaannames extra verbruik: ' + i.devices.map(device => deviceLabels[device] + ' ' + nf.format(i.deviceKwh[device]) + ' kWh/jaar').join('; ') + '.');
    if (!estimate.fields.includes('annualImport') && i.devices?.length) parts.push('Grote verbruikers niet nogmaals opgeteld: de bekende totale netafname is behouden.');
    parts.push('Geschatte waarden afgerond op circa 50 kWh; bekende jaarcijfers behouden.');
    if (estimate.adjusted?.length) parts.push('Daarna handmatig bijgesteld: ' + estimate.adjusted.map(name => labels[name]).join(', ') + '. Nog te controleren met de jaargegevens.');
    return parts.join(' ');
  }

  function sanitizeStoredEstimate(raw) {
    if (!raw || !Array.isArray(raw.fields) || !raw.input) return null;
    const estimated = [...new Set(raw.fields.filter(name => fields.includes(name)))];
    if (!estimated.length) return null;
    const input = Object.fromEntries(['people', 'panels', 'wp', 'yieldFactor', 'selfUse', 'householdUpliftPercent'].map(name => [name, number(raw.input[name])]));
    if (!Object.hasOwn(household, input.people) || !Number.isInteger(input.panels) || input.panels < 0 || input.panels > 60 ||
        !Number.isFinite(input.wp) || input.wp < 100 || input.wp > 600 || ![.75, .85, .95].includes(input.yieldFactor) ||
        !Number.isFinite(input.selfUse) || input.selfUse < 10 || input.selfUse > 70 ||
        !Number.isFinite(input.householdUpliftPercent) || input.householdUpliftPercent < 0 || input.householdUpliftPercent > 100) return null;
    try { Object.assign(input, deviceEstimate(raw.input)); } catch (_) { return null; }
    return { fields: estimated, input, adjusted: Array.isArray(raw.adjusted) ? raw.adjusted.filter(name => estimated.includes(name)) : [] };
  }

  function bind(controller) {
    const root = controller.root, form = controller.form;
    const find = name => root.querySelector('[data-zv-estimate' + (name ? '-' + name : '') + ']');
    const helper = find('');
    if (!helper) return;
    const field = name => form.elements.namedItem(name);
    const selectedDevices = () => [...helper.querySelectorAll('[name="estimateDevices"]:checked')].map(el => el.value);
    const deviceInputs = [...helper.querySelectorAll('[data-zv-estimate-device-kwh]')];
    const readDevices = () => ({ devices: selectedDevices(), deviceKwh: Object.fromEntries(deviceInputs.map(el => [el.dataset.zvEstimateDeviceKwh, el.value])) });
    const syncDevices = () => {
      const devices = selectedDevices();
      find('device-estimates').hidden = !devices.length;
      deviceInputs.forEach(input => {
        const selected = devices.includes(input.dataset.zvEstimateDeviceKwh);
        input.closest('[data-zv-estimate-device-field]').hidden = !selected;
        input.disabled = !selected;
      });
      try {
        const { extraKwh } = deviceEstimate(readDevices());
        find('device-total').textContent = 'Samen circa ' + nf.format(extraKwh) + ' kWh extra per jaar. Alleen toegevoegd als de netafname wordt geschat.';
      } catch (_) { find('device-total').textContent = 'Vul het extra jaarverbruik in voor de gekozen apparaten.'; }
    };
    const invalidate = () => { find('error').hidden = true; find('status').textContent = ''; };
    const sync = () => {
      const estimate = controller.adviceEstimate;
      fields.forEach(name => {
        const estimated = Boolean(estimate?.fields.includes(name));
        field(name).closest('.zv-sc__field')?.classList.toggle('is-estimated', estimated);
        if (estimated) field(name).setAttribute('aria-description', 'Geschat jaarcijfer; controleer later met de jaargegevens.');
        else field(name).removeAttribute('aria-description');
      });
    };
    const persist = () => controller.saveState?.();
    const clear = () => { controller.adviceEstimate = null; invalidate(); sync(); persist(); };
    let demoFields = new Set();
    form.addEventListener('input', event => {
      if (fields.includes(event.target.name)) {
        // Keep the provenance of revised estimates.
        // A manually replaced example value, however, must never be overwritten.
        demoFields.delete(event.target.name);
        if (controller.adviceEstimate?.fields.includes(event.target.name)) controller.adviceEstimate.adjusted = [...new Set([...(controller.adviceEstimate.adjusted || []), event.target.name])];
        invalidate(); sync(); persist();
      } else if (event.target.name?.startsWith('estimate')) { invalidate(); syncDevices(); }
    });
    helper.addEventListener('change', () => {
      invalidate();
      syncDevices();
    });
    find('calculate').addEventListener('click', () => {
      invalidate();
      try {
        const current = Object.fromEntries(fields.map(name => [name, field(name).value]));
        const known = knownValues(current, controller.adviceEstimate, root.dataset.adviceDemo === 'true' ? [...demoFields] : []);
        const applied = calculate({ people: field('estimatePeople').value, panels: field('estimatePanels').value, ...defaults, ...readDevices() }, known);
        applied.fields.forEach(name => { field(name).value = applied.values[name]; });
        controller.adviceEstimate = applied;
        root.dataset.adviceDemo = 'false'; demoFields.clear();
        root.querySelectorAll('[data-zv-advice-demo]').forEach(el => { el.hidden = true; });
        controller.clearInvalidState(); controller.showMessages([], []); controller.syncStepValidity();
        invalidate(); sync(); persist(); helper.open = false;
        field('annualImport').focus();
      } catch (error) { find('error').textContent = error.message; find('error').hidden = false; }
    });
    root.querySelector('[data-zv-sc-example]')?.addEventListener('click', () => { clear(); demoFields = new Set(fields); });
    form.addEventListener('reset', () => {
      controller.adviceEstimate = null; invalidate(); sync(); demoFields.clear(); helper.open = false;
      helper.querySelectorAll('details').forEach(el => { el.open = false; });
      // The reset event precedes the browser restoring default checkbox values.
      queueMicrotask(syncDevices);
    });
    const restored = sanitizeStoredEstimate(controller.savedState?.estimate);
    if (restored) {
      controller.adviceEstimate = restored;
      field('estimatePeople').value = restored.input.people;
      field('estimatePanels').value = restored.input.panels;
      helper.querySelectorAll('[name="estimateDevices"]').forEach(el => { el.checked = restored.input.devices.includes(el.value); });
      deviceInputs.forEach(el => { if (Object.hasOwn(restored.input.deviceKwh, el.dataset.zvEstimateDeviceKwh)) el.value = restored.input.deviceKwh[el.dataset.zvEstimateDeviceKwh]; });
    }
    syncDevices(); sync();
  }
  window.ZVSalderingscheckEstimate = { defaults, calculate, describe, bind, deviceEstimate, knownValues, sanitizeStoredEstimate };
})();
